import { Button, Divider, IconButton, Paper, Typography } from '@mui/material';
import { UPDATE_CUSTOMER_SECTIONS_TO_SUBMIT, type UpdateCustomer, type UpdateCustomerSectionToSubmit } from '@weft/types';
import { EditIcon } from 'apps/app/src/components/icons';
import { useActivate } from 'apps/app/src/hooks/use-activate';
import { useIsLoadingMutation } from 'apps/app/src/hooks/use-is-loading-mutation';
import cn from 'classnames';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

type CustomerViewPropertiesBlockProps = {
  title: string,
  sectionToSubmit?: UpdateCustomerSectionToSubmit;
  children: React.ReactNode;
  className?: string,
  childrenContainerClassName?: string,
  titleClassName?: string,
  showEditButton?: boolean,
  onEditChange?: (isInEditMode: boolean) => void,
  onCancel?: () => void,
  onSave?: () => void,
};

export const CustomerViewPropertiesBlock: React.FC<CustomerViewPropertiesBlockProps> = ({
  title,
  children,
  className: externalClassName,
  childrenContainerClassName: externalChildrenContainerClassName,
  titleClassName: externalTitleClassName,
  showEditButton = false,
  onEditChange,
  sectionToSubmit,
  onCancel,
  onSave,
}) => {
  const [isInEditMode, setEditMode, unsetEditMode] = useActivate(false);
  const { setValue } = useFormContext<UpdateCustomer>();
  const isUpdating = useIsLoadingMutation('update-customer');

  const handleEditClick = () => {
    setEditMode(onEditChange);
  };

  const handleDoneClick = () => {
    unsetEditMode(onEditChange);
    onSave?.();
        
    setValue('sectionToSubmit', sectionToSubmit ?? UPDATE_CUSTOMER_SECTIONS_TO_SUBMIT.none);
  };

  const handleCancelClick = () => {
    unsetEditMode(onEditChange);
    onCancel?.();
  };

  return (
    <Paper
      variant='outlined'
      className={twMerge('flex flex-col pt-9', externalClassName)}
      sx={{ padding: '42px 32px' }}
    >
      <div className='flex items-center justify-between'>      
        <Typography
          variant='h2'
          className={twMerge('text-xl font-medium', externalTitleClassName)}
        >
          {title}
        </Typography>
        <div className={cn(!showEditButton && 'invisible', 'min-h-[36px]')}>
          {!isInEditMode ? (
            <IconButton
              onClick={handleEditClick}
            >
              <EditIcon className='fill-icon'/>
            </IconButton>
          ) : (<>
            <Button
              variant='text'
              className='px-2 py-0 text-base disabled:cursor-default disabled:opacity-50'
              disabled={isUpdating}
              onClick={handleCancelClick}
            >
              Cancel
            </Button>
            <Button
              type='submit'
              variant='text'
              className='px-2 py-0 text-base disabled:cursor-default disabled:opacity-50'
              disabled={isUpdating}
              onClick={handleDoneClick}
            >
              Save
            </Button>
          </>
          )}
        </div>

      </div>
      <Divider className='my-7'/>
      <div className={twMerge('flex flex-wrap justify-start', externalChildrenContainerClassName)}>
        {children}
      </div>
    </Paper>
  );
};